import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Check, X, AlertTriangle, Users, Star, Calendar, DollarSign } from 'lucide-react';
import EnhancedCard from '@/components/enhanced/EnhancedCard';

export default function ServiceManagement() {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('services');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    const mockServices = [
      {
        id: 'service-1',
        title: 'Professional House Cleaning',
        description: 'Comprehensive house cleaning service using eco-friendly products.',
        category: 'home-maintenance',
        providerId: 'provider-1',
        providerName: 'Maria Santos',
        pricing: { type: 'hourly', amount: 250 },
        status: 'pending',
        featured: false,
        images: ['/api/placeholder/300/200'],
        createdAt: '2024-01-10',
        submittedDocuments: ['business-license', 'insurance', 'certifications']
      },
      {
        id: 'service-2',
        title: 'Garden Maintenance',
        description: 'Complete garden care including pruning, planting, and landscape design.',
        category: 'home-maintenance',
        providerId: 'provider-2',
        providerName: 'Juan Cruz',
        pricing: { type: 'fixed', amount: 1200 },
        status: 'active',
        featured: true,
        images: ['/api/placeholder/300/200'],
        rating: 4.7,
        reviewCount: 23,
        createdAt: '2023-12-15',
        approvedAt: '2023-12-16'
      },
      {
        id: 'service-3',
        title: 'Computer Repair',
        description: 'Professional computer and laptop repair services.',
        category: 'professional',
        providerId: 'provider-3',
        providerName: 'Tech Solutions Inc.',
        pricing: { type: 'hourly', amount: 500 },
        status: 'rejected',
        featured: false,
        images: ['/api/placeholder/300/200'],
        createdAt: '2024-01-05',
        rejectedAt: '2024-01-06',
        rejectionReason: 'Insufficient documentation provided'
      }
    ];

    const mockProviders = [
      {
        id: 'provider-1',
        businessName: 'Santos Cleaning Services',
        contactPerson: 'Maria Santos',
        email: 'maria@santoscleaning.com',
        phone: '+63 917 123 4567',
        status: 'pending',
        verified: false,
        rating: 0,
        serviceCount: 1,
        completedJobs: 0,
        createdAt: '2024-01-10',
        documents: ['business-license', 'insurance', 'id-verification']
      },
      {
        id: 'provider-2',
        businessName: 'Green Thumb Gardens',
        contactPerson: 'Juan Cruz',
        email: 'juan@greenthumb.com',
        phone: '+63 917 234 5678',
        status: 'active',
        verified: true,
        rating: 4.7,
        serviceCount: 3,
        completedJobs: 45,
        createdAt: '2023-12-15',
        verifiedAt: '2023-12-16'
      },
      {
        id: 'provider-3',
        businessName: 'Tech Solutions Inc.',
        contactPerson: 'Carlos Reyes',
        email: 'carlos@techsolutions.com',
        phone: '+63 917 345 6789',
        status: 'suspended',
        verified: false,
        rating: 3.2,
        serviceCount: 2,
        completedJobs: 12,
        createdAt: '2023-11-20',
        suspendedAt: '2024-01-08',
        suspensionReason: 'Multiple customer complaints'
      }
    ];

    const mockReviews = [
      {
        id: 'review-1',
        serviceId: 'service-2',
        serviceTitle: 'Garden Maintenance',
        customerId: 'customer-1',
        customerName: 'Anna Cruz',
        providerId: 'provider-2',
        providerName: 'Juan Cruz',
        rating: 5,
        review: 'Excellent service! Very professional and thorough work.',
        approved: false,
        createdAt: '2024-01-15',
        images: []
      },
      {
        id: 'review-2',
        serviceId: 'service-2',
        serviceTitle: 'Garden Maintenance',
        customerId: 'customer-2',
        customerName: 'Robert Santos',
        providerId: 'provider-2',
        providerName: 'Juan Cruz',
        rating: 4,
        review: 'Good work, but could be more punctual.',
        approved: true,
        createdAt: '2024-01-12',
        approvedAt: '2024-01-13',
        images: []
      },
      {
        id: 'review-3',
        serviceId: 'service-3',
        serviceTitle: 'Computer Repair',
        customerId: 'customer-3',
        customerName: 'Lisa Garcia',
        providerId: 'provider-3',
        providerName: 'Tech Solutions Inc.',
        rating: 2,
        review: 'Service was delayed and communication was poor.',
        approved: false,
        reported: true,
        reportReason: 'Inappropriate content',
        createdAt: '2024-01-08',
        images: []
      }
    ];

    setTimeout(() => {
      setServices(mockServices);
      setProviders(mockProviders);
      setReviews(mockReviews);
      setLoading(false);
    }, 1000);
  }, []);

  const handleApproveService = async (serviceId) => {
    try {
      setServices(prev => prev.map(service => 
        service.id === serviceId 
          ? { ...service, status: 'active', approvedAt: new Date().toISOString() }
          : service
      ));
      notify('Service approved successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to approve service', { type: 'error' });
    }
  };

  const handleRejectService = async (serviceId, reason) => {
    try {
      setServices(prev => prev.map(service => 
        service.id === serviceId 
          ? { 
              ...service, 
              status: 'rejected', 
              rejectedAt: new Date().toISOString(),
              rejectionReason: reason 
            }
          : service
      ));
      notify('Service rejected', { type: 'success' });
    } catch (error) {
      notify('Failed to reject service', { type: 'error' });
    }
  };

  const handleVerifyProvider = async (providerId) => {
    try {
      setProviders(prev => prev.map(provider => 
        provider.id === providerId 
          ? { 
              ...provider, 
              status: 'active',
              verified: true, 
              verifiedAt: new Date().toISOString() 
            }
          : provider
      ));
      notify('Provider verified successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to verify provider', { type: 'error' });
    }
  };

  const handleSuspendProvider = async (providerId, reason) => {
    try {
      setProviders(prev => prev.map(provider => 
        provider.id === providerId 
          ? { 
              ...provider, 
              status: 'suspended',
              suspendedAt: new Date().toISOString(),
              suspensionReason: reason 
            }
          : provider
      ));
      notify('Provider suspended', { type: 'success' });
    } catch (error) {
      notify('Failed to suspend provider', { type: 'error' });
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, approved: true, approvedAt: new Date().toISOString() }
          : review
      ));
      notify('Review approved', { type: 'success' });
    } catch (error) {
      notify('Failed to approve review', { type: 'error' });
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      setReviews(prev => prev.filter(review => review.id !== reviewId));
      notify('Review rejected and removed', { type: 'success' });
    } catch (error) {
      notify('Failed to reject review', { type: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = () => {
    let data = [];
    if (activeTab === 'services') data = services;
    else if (activeTab === 'providers') data = providers;
    else if (activeTab === 'reviews') data = reviews;

    // Filter by status
    if (filterStatus !== 'all') {
      data = data.filter(item => item.status === filterStatus || (filterStatus === 'unapproved' && !item.approved));
    }

    // Filter by search term
    if (searchTerm) {
      data = data.filter(item => 
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.businessName && item.businessName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.contactPerson && item.contactPerson.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.review && item.review.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return data;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Management</h1>
          <p className="text-gray-600 mt-1">Manage services, providers, and reviews</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <EnhancedCard className="p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {services.filter(s => s.status === 'pending').length}
          </div>
          <div className="text-gray-600">Pending Services</div>
        </EnhancedCard>
        
        <EnhancedCard className="p-6 text-center">
          <div className="text-3xl font-bold text-yellow-600 mb-2">
            {providers.filter(p => p.status === 'pending').length}
          </div>
          <div className="text-gray-600">Pending Providers</div>
        </EnhancedCard>
        
        <EnhancedCard className="p-6 text-center">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {reviews.filter(r => !r.approved).length}
          </div>
          <div className="text-gray-600">Pending Reviews</div>
        </EnhancedCard>
        
        <EnhancedCard className="p-6 text-center">
          <div className="text-3xl font-bold text-red-600 mb-2">
            {reviews.filter(r => r.reported).length}
          </div>
          <div className="text-gray-600">Reported Reviews</div>
        </EnhancedCard>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'services', label: 'Services', count: services.length },
            { id: 'providers', label: 'Providers', count: providers.length },
            { id: 'reviews', label: 'Reviews', count: reviews.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
          {activeTab === 'reviews' && <option value="unapproved">Unapproved</option>}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-white rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="h-6 bg-gray-200 rounded w-20"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredData().map((item) => (
            <EnhancedCard key={item.id} className="p-6">
              {activeTab === 'services' && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex-1 mb-4 md:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                      {item.featured && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                          Featured
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{item.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Provider: {item.providerName}</span>
                      <span>₱{item.pricing.amount} {item.pricing.type === 'hourly' ? '/hour' : 'fixed'}</span>
                      <span>Created: {new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                    {item.rejectionReason && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        Rejection reason: {item.rejectionReason}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        setShowDetailModal(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5 text-gray-600" />
                    </button>
                    
                    {item.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproveService(item.id)}
                          className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <Check className="w-5 h-5 text-green-600" />
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Enter rejection reason:');
                            if (reason) handleRejectService(item.id, reason);
                          }}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <X className="w-5 h-5 text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'providers' && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex-1 mb-4 md:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{item.businessName}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                      {item.verified && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          Verified
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                      <span>Contact: {item.contactPerson}</span>
                      <span>Email: {item.email}</span>
                      <span>Phone: {item.phone}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{item.serviceCount} services</span>
                      <span>{item.completedJobs} completed jobs</span>
                      {item.rating > 0 && <span>{item.rating} ⭐ rating</span>}
                      <span>Joined: {new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                    {item.suspensionReason && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        Suspension reason: {item.suspensionReason}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        setShowDetailModal(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5 text-gray-600" />
                    </button>
                    
                    {item.status === 'pending' && (
                      <button
                        onClick={() => handleVerifyProvider(item.id)}
                        className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                        title="Verify Provider"
                      >
                        <Check className="w-5 h-5 text-green-600" />
                      </button>
                    )}
                    
                    {item.status === 'active' && (
                      <button
                        onClick={() => {
                          const reason = prompt('Enter suspension reason:');
                          if (reason) handleSuspendProvider(item.id, reason);
                        }}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        title="Suspend Provider"
                      >
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1 mb-4 md:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{item.serviceTitle}</h3>
                      <div className="flex items-center">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < item.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                      {!item.approved && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                          Pending Approval
                        </span>
                      )}
                      {item.reported && (
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                          Reported
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 mb-2">"{item.review}"</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>By: {item.customerName}</span>
                      <span>Provider: {item.providerName}</span>
                      <span>Date: {new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                    {item.reportReason && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        Report reason: {item.reportReason}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!item.approved && (
                      <>
                        <button
                          onClick={() => handleApproveReview(item.id)}
                          className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                          title="Approve Review"
                        >
                          <Check className="w-5 h-5 text-green-600" />
                        </button>
                        <button
                          onClick={() => handleRejectReview(item.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                          title="Reject Review"
                        >
                          <X className="w-5 h-5 text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </EnhancedCard>
          ))}
        </div>
      )}
    </div>
  );
}
